import React, { useEffect, useState, useCallback } from 'react';
import { useFloatMessage, MESSAGE_TYPE } from './../../../Providers/FloatMessage';
import { SecondaryBase } from '../../Commons/SecondaryBase';
import { Confirm } from '../../../Commons/Modals/Confirm';
import {
    GetAdminMetrics,
    GetAdminParticipants,
    GetAdminConfig,
    UpdateAdminConfig,
    GetExportDataset,
    ExportXtpReportToExcel,
    UndisqualifyParticipant,
    UnblockTicket,
    UnblockSession,
    FormatMexicoDate,
    FormatTijuanaDateTime,
    RedeemParticipant,
    UnredeemParticipant,
    PlayXtpAudio,
    DownloadPassCard
} from '../../../../utilities/BL/Xtp';
import { XtpConfigModal } from './XtpConfigModal';
import { XtpDisqualifyModal } from './XtpDisqualifyModal';
import { XtpCheckinModal } from './XtpCheckinModal';
import { XtpResetModal } from './XtpResetModal';
import { XtpResetRedeemedModal } from './XtpResetRedeemedModal';
import { XtpResetPassTpxModal } from './XtpResetPassTpxModal';
import { XtpGeneratePassModal } from './XtpGeneratePassModal';

export const XtpBoard = ({ getOutOfTheDynamic }) => {
    const { showMessage } = useFloatMessage();

    // Data States
    const [metrics, setMetrics] = useState({
        totalQrScans: 0,
        totalPageViews: 0,
        totalFormStarts: 0,
        totalRegistersCompleted: 0,
        totalPassesSaved: 0,
        totalPassesRecovered: 0,
        totalDisqualified: 0,
        totalRedeemed: 0,
        conversionRatePercent: 0,
        passFan: {
            totalQrScans: 0,
            totalPageViews: 0,
            totalSearchStarts: 0,
            totalSearchQueries: 0,
            totalPassesFound: 0,
            totalNotFound: 0,
            totalPassesSaved: 0,
            searchSuccessRatePercent: 0
        }
    });
    const [participants, setParticipants] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize] = useState(25);
    const [totalPages, setTotalPages] = useState(1);

    // Dynamic Configuration
    const [config, setConfig] = useState({
        isCampaignActive: true,
        isPassLookupActive: true,
        passCodePrefix: '#TPX26-',
        campaignTitle: 'Tradiciones Perronas — Boletos Xolos Femenil',
        campaignSubtitle: 'Club Tijuana Xoloitzcuintles de Caliente',
        maxQuota: 333,
        randomWinnersCount: 20
    });

    // Filters and Sorting
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('ALL');
    const [sortBy, setSortBy] = useState('createdAtUtc');
    const [sortOrder, setSortOrder] = useState('desc');

    // Loaders
    const [isLoading, setIsLoading] = useState(false);
    const [isExportingExcel, setIsExportingExcel] = useState(false);
    const [lastUpdated, setLastUpdated] = useState('');

    // Modals
    const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
    const [disqualifyTarget, setDisqualifyTarget] = useState(null);
    const [isCheckinModalOpen, setIsCheckinModalOpen] = useState(false);
    const [isResetModalOpen, setIsResetModalOpen] = useState(false);
    const [isResetRedeemedModalOpen, setIsResetRedeemedModalOpen] = useState(false);
    const [passModalParticipant, setPassModalParticipant] = useState(null);
    const [passModalIndex, setPassModalIndex] = useState(-1);

    // Confirm Modal State
    const [showConfirm, setShowConfirm] = useState(false);
    const [confirmConfig, setConfirmConfig] = useState({
        message: '',
        textYes: 'Sí, confirmar',
        textNo: 'Cancelar',
        action: () => {}
    });

    // Collapsible Sections State
    const [isRegistrationControlOpen, setIsRegistrationControlOpen] = useState(true);
    const [isDangerZoneOpen, setIsDangerZoneOpen] = useState(false);
    const [isToolsOpen, setIsToolsOpen] = useState(false);
    const [isPassTpxMetricsOpen, setIsPassTpxMetricsOpen] = useState(true);
    const [isPassLookupControlOpen, setIsPassLookupControlOpen] = useState(true);
    const [isPassTpxDangerZoneOpen, setIsPassTpxDangerZoneOpen] = useState(false);
    const [isResetPassTpxModalOpen, setIsResetPassTpxModalOpen] = useState(false);

    // Quick Unblock Drawer State
    const [unblockTicketQuery, setUnblockTicketQuery] = useState('');
    const [unblockSessionQuery, setUnblockSessionQuery] = useState('');
    const [isUnblocking, setIsUnblocking] = useState(false);
    const [generatingPassId, setGeneratingPassId] = useState(null);
    const [isRefreshingPassFanMetrics, setIsRefreshingPassFanMetrics] = useState(false);

    // Descargar imagen oficial del pase de un participante
    const handleDownloadParticipantPass = async (participant) => {
        if (!participant || generatingPassId) return;
        setGeneratingPassId(participant.id);
        try {
            const success = await DownloadPassCard(participant, {
                bannerTitle: participant.isWinner ? '★ PASE DE GANADOR OFICIAL ★' : 'PASE EXCLUSIVO PERSONAL'
            });
            if (success) {
                showMessage({
                    message: `Imagen del pase de ${participant.name} descargada exitosamente.`,
                    type: MESSAGE_TYPE.SUCCESS
                });
            } else {
                showMessage({
                    message: 'No fue posible generar la imagen del pase.',
                    type: MESSAGE_TYPE.ERROR
                });
            }
        } catch (err) {
            console.error('Error al generar pase desde admin:', err);
            showMessage({
                message: 'Error al generar la imagen del pase.',
                type: MESSAGE_TYPE.ERROR
            });
        } finally {
            setGeneratingPassId(null);
        }
    };

    // Cargar configuración activa
    const loadConfig = useCallback(async () => {
        try {
            const res = await GetAdminConfig();
            if (res && res.success && res.data) {
                setConfig(prev => ({
                    ...prev,
                    ...res.data,
                    isCampaignActive: res.data.isCampaignActive ?? true,
                    isPassLookupActive: res.data.isPassLookupActive ?? true,
                    passCodePrefix: res.data.passCodePrefix || '#TPX26-'
                }));
            }
        } catch (err) {
            console.error('Error al cargar configuración de XTP:', err);
        }
    }, []);

    // Cargar métricas
    const loadMetrics = useCallback(async () => {
        try {
            const res = await GetAdminMetrics();
            if (res && res.success && res.data) {
                setMetrics(res.data);
            }
        } catch (err) {
            console.error('Error al cargar métricas:', err);
        }
    }, []);

    // Actualizar métricas independientes del portal /passtpx
    const handleRefreshPassFanMetrics = async (e) => {
        if (e) e.stopPropagation();
        if (isRefreshingPassFanMetrics) return;
        setIsRefreshingPassFanMetrics(true);
        try {
            await loadMetrics();
            showMessage({
                message: 'Estadísticas del portal /passtpx actualizadas.',
                type: MESSAGE_TYPE.SUCCESS
            });
        } catch (err) {
            console.error('Error al actualizar métricas:', err);
            showMessage({
                message: 'Error de red al actualizar estadísticas.',
                type: MESSAGE_TYPE.ERROR
            });
        } finally {
            setIsRefreshingPassFanMetrics(false);
        }
    };

    // Cargar participantes paginados y ordenados
    const loadParticipants = useCallback(async (
        targetPage = 1,
        search = searchTerm,
        status = statusFilter,
        field = sortBy,
        order = sortOrder
    ) => {
        setIsLoading(true);
        try {
            const res = await GetAdminParticipants(targetPage, pageSize, search, status, field, order);
            if (res && res.success && res.data) {
                setParticipants(res.data.items || []);
                setTotalCount(res.data.totalCount || 0);
                setPage(res.data.page || targetPage);
                setTotalPages(res.data.totalPages || 1);
            }
            setLastUpdated(new Date().toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) + ' hrs');
        } catch (err) {
            console.error('Error al cargar participantes:', err);
            showMessage({
                message: 'No fue posible cargar el padrón de participantes.',
                type: MESSAGE_TYPE.ERROR
            });
        } finally {
            setIsLoading(false);
        }
    }, [pageSize, searchTerm, statusFilter, sortBy, sortOrder, showMessage]);

    const handleSort = (field) => {
        const isSameField = sortBy === field;
        const newOrder = isSameField && sortOrder === 'desc' ? 'asc' : 'desc';
        setSortBy(field);
        setSortOrder(newOrder);
        loadParticipants(1, searchTerm, statusFilter, field, newOrder);
    };

    const refreshAllData = useCallback(() => {
        loadConfig();
        loadMetrics();
        loadParticipants(page, searchTerm, statusFilter, sortBy, sortOrder);
    }, [loadConfig, loadMetrics, loadParticipants, page, searchTerm, statusFilter, sortBy, sortOrder]);

    useEffect(() => {
        loadConfig();
        loadMetrics();
        loadParticipants(1, searchTerm, statusFilter, sortBy, sortOrder);
    }, [loadConfig, loadMetrics, loadParticipants]);

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        loadParticipants(1, searchTerm, statusFilter, sortBy, sortOrder);
    };

    const handleClearSearch = () => {
        setSearchTerm('');
        loadParticipants(1, '', statusFilter, sortBy, sortOrder);
    };

    const handleStatusFilterChange = (newStatus) => {
        setStatusFilter(newStatus);
        loadParticipants(1, searchTerm, newStatus, sortBy, sortOrder);
    };

    // Alternar apertura/cierre de registros de campaña
    const triggerToggleCampaignActive = (nextState) => {
        setConfirmConfig({
            message: `¿Estás seguro de que deseas ${nextState ? 'ABRIR' : 'PAUSAR / CERRAR'} el registro de tickets para los aficionados?`,
            textYes: nextState ? 'Sí, abrir registros' : 'Sí, pausar registros',
            textNo: 'Cancelar',
            action: async () => {
                showMessage({
                    type: MESSAGE_TYPE.INFO,
                    message: 'Actualizando estado de la dinámica...'
                });
                try {
                    const res = await UpdateAdminConfig({ ...config, isCampaignActive: nextState });
                    if (res && res.success) {
                        setConfig(prev => ({ ...prev, isCampaignActive: nextState }));
                        showMessage({
                            type: MESSAGE_TYPE.SUCCESS,
                            message: `Registros ${nextState ? 'abiertos (recibiendo participaciones)' : 'pausados (formulario cerrado)'} con éxito.`
                        });
                        return { success: true };
                    } else {
                        showMessage({
                            type: MESSAGE_TYPE.ERROR,
                            message: res?.message || 'Error al actualizar estado.'
                        });
                        return { success: false };
                    }
                } catch (e) {
                    showMessage({
                        type: MESSAGE_TYPE.ERROR,
                        message: 'Error de red al actualizar estado de la dinámica.'
                    });
                    return { success: false };
                }
            }
        });
        setShowConfirm(true);
    };

    // Alternar apertura/pausa de consultas de pases en /passtpx
    const triggerTogglePassLookupActive = (nextState) => {
        setConfirmConfig({
            message: `¿Estás seguro de que deseas ${nextState ? 'HABILITAR' : 'PAUSAR / INHABILITAR'} la consulta de pases en /passtpx para los aficionados?`,
            textYes: nextState ? 'Sí, habilitar consultas' : 'Sí, pausar consultas',
            textNo: 'Cancelar',
            action: async () => {
                showMessage({
                    type: MESSAGE_TYPE.INFO,
                    message: 'Actualizando disponibilidad de consultas...'
                });
                try {
                    const res = await UpdateAdminConfig({ ...config, isPassLookupActive: nextState });
                    if (res && res.success) {
                        setConfig(prev => ({ ...prev, isPassLookupActive: nextState }));
                        showMessage({
                            type: MESSAGE_TYPE.SUCCESS,
                            message: `Consultas en /passtpx ${nextState ? 'habilitadas con éxito.' : 'pausadas con éxito.'}`
                        });
                        return { success: true };
                    } else {
                        showMessage({
                            type: MESSAGE_TYPE.ERROR,
                            message: res?.message || 'Error al actualizar disponibilidad.'
                        });
                        return { success: false };
                    }
                } catch (e) {
                    showMessage({
                        type: MESSAGE_TYPE.ERROR,
                        message: 'Error de red al actualizar estado de consultas.'
                    });
                    return { success: false };
                }
            }
        });
        setShowConfirm(true);
    };

    // Exportación oficial en Excel de 2 Bloques
    const handleExportExcel = async () => {
        setIsExportingExcel(true);
        try {
            const res = await GetExportDataset();
            if (res && res.success && res.data) {
                const exported = await ExportXtpReportToExcel(res.data);
                if (exported) {
                    showMessage({
                        message: '¡Reporte oficial en Excel descargado exitosamente!',
                        type: MESSAGE_TYPE.SUCCESS
                    });
                } else {
                    showMessage({
                        message: 'No se encontraron datos de participantes para exportar.',
                        type: MESSAGE_TYPE.WARNING
                    });
                }
            } else {
                showMessage({
                    message: 'Error al consultar el dataset oficial para Excel.',
                    type: MESSAGE_TYPE.ERROR
                });
            }
        } catch (err) {
            console.error('Error al exportar Excel:', err);
            showMessage({
                message: 'Error inesperado al generar el archivo Excel.',
                type: MESSAGE_TYPE.ERROR
            });
        } finally {
            setIsExportingExcel(false);
        }
    };

    // Restituir participante
    const handleUndisqualify = async (participant) => {
        if (!window.confirm(`¿Deseas restituir a ${participant.name}? Su código QR volverá a estar activo.`)) {
            return;
        }

        try {
            const res = await UndisqualifyParticipant(participant.id, 'Staff Admin Interaktia');
            if (res && res.success) {
                showMessage({
                    message: `Participante ${participant.name} restituido exitosamente.`,
                    type: MESSAGE_TYPE.SUCCESS
                });
                loadParticipants(page);
                loadMetrics();
            } else {
                showMessage({
                    message: res?.message || 'Error al restituir participante.',
                    type: MESSAGE_TYPE.ERROR
                });
            }
        } catch (err) {
            console.error('Error al restituir:', err);
            showMessage({
                message: 'Error de conexión al restituir participante.',
                type: MESSAGE_TYPE.ERROR
            });
        }
    };

    // Desmarcar / Revertir Canje de participante individual (Modo Pruebas / Corrección)
    const handleUnredeem = async (participant) => {
        const confirmMsg = `CONFIRMACIÓN DE SEGURIDAD:\n\n¿Estás seguro de que deseas desmarcar el canje de "${participant.name}" (Folio: ${participant.passCode})?\n\nEl boleto volverá a figurar como "PENDIENTE / ACTIVO" para poder volver a ser validado en accesos.`;
        if (!window.confirm(confirmMsg)) {
            return;
        }

        try {
            const res = await UnredeemParticipant({
                participantId: participant.id,
                passCode: participant.passCode,
                operatorName: 'Staff Admin Interaktia'
            });
            if (res && res.success) {
                showMessage({
                    message: `Canje revertido para ${participant.name}. Estado: Pendiente.`,
                    type: MESSAGE_TYPE.SUCCESS
                });
                loadParticipants(page);
                loadMetrics();
            } else {
                showMessage({
                    message: res?.message || 'Error al desmarcar el canje.',
                    type: MESSAGE_TYPE.ERROR
                });
            }
        } catch (err) {
            console.error('Error al desmarcar canje:', err);
            showMessage({
                message: 'Error de conexión al desmarcar el canje.',
                type: MESSAGE_TYPE.ERROR
            });
        }
    };

    // Desbloqueo rápido de Ticket
    const handleQuickUnblockTicket = async (e) => {
        e.preventDefault();
        if (!unblockTicketQuery.trim()) return;

        setIsUnblocking(true);
        try {
            const res = await UnblockTicket(unblockTicketQuery.trim(), 'Desbloqueo manual desde tablero');
            if (res && res.success) {
                showMessage({
                    message: res.message || `Ticket '${unblockTicketQuery}' desbloqueado exitosamente.`,
                    type: MESSAGE_TYPE.SUCCESS
                });
                setUnblockTicketQuery('');
            } else {
                showMessage({
                    message: res?.message || 'No se encontró suspensión activa para ese ticket.',
                    type: MESSAGE_TYPE.WARNING
                });
            }
        } catch (err) {
            console.error('Error al desbloquear ticket:', err);
            showMessage({
                message: 'Error al conectar para desbloquear ticket.',
                type: MESSAGE_TYPE.ERROR
            });
        } finally {
            setIsUnblocking(false);
        }
    };

    // Desbloqueo rápido de Sesión
    const handleQuickUnblockSession = async (e) => {
        e.preventDefault();
        if (!unblockSessionQuery.trim()) return;

        setIsUnblocking(true);
        try {
            const res = await UnblockSession(unblockSessionQuery.trim(), 'Desbloqueo manual desde tablero');
            if (res && res.success) {
                showMessage({
                    message: res.message || `Sesión desbloqueada exitosamente.`,
                    type: MESSAGE_TYPE.SUCCESS
                });
                setUnblockSessionQuery('');
            } else {
                showMessage({
                    message: res?.message || 'No se encontró suspensión activa para esa sesión.',
                    type: MESSAGE_TYPE.WARNING
                });
            }
        } catch (err) {
            console.error('Error al desbloquear sesión:', err);
            showMessage({
                message: 'Error al conectar para desbloquear sesión.',
                type: MESSAGE_TYPE.ERROR
            });
        } finally {
            setIsUnblocking(false);
        }
    };

    // Conteo estimado de ganadores
    const winnersCount = participants.filter(p => p.isWinner && !p.isDisqualified).length;
    const passFan = metrics?.passFanMetrics || metrics?.passFan || {};

    return (
        <SecondaryBase onBack={getOutOfTheDynamic}>
            <div className="container-fluid px-4 py-3 XtpBoard">
                {/* 1. ENCABEZADO PRINCIPAL (ESTILO MVP XOLOS ZONE) */}
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4 border-bottom pb-3">
                    <div>
                        <h1 className="h3 fw-bold text-dark mb-1">
                            <i className="bi bi-gift-fill text-danger me-2"></i>
                            Tradiciones Perronas — Boletos Xolos Femenil
                        </h1>
                        <p className="text-secondary small mb-0 d-flex align-items-center gap-2 flex-wrap">
                            <span>{config.campaignSubtitle || 'Club Tijuana Xoloitzcuintles de Caliente'} — Prefijo: <strong>{config.passCodePrefix || '#TPX26-'}</strong></span>
                            <span className={`badge ${config.isCampaignActive ? 'bg-success' : 'bg-danger'} text-white fw-semibold`}>
                                {config.isCampaignActive ? '🟢 Registros Activos' : '🔴 Registros Pausados'}
                            </span>
                            <span className="badge bg-dark text-white fw-semibold">
                                🎟️ Jersey Alterno Día de Muertos (Cupo 333)
                            </span>
                            {lastUpdated && (
                                <span className="badge bg-light text-muted border">
                                    Actualizado: {lastUpdated}
                                </span>
                            )}
                        </p>
                    </div>

                    <div className="d-flex align-items-center gap-2 flex-wrap">
                        <button
                            type="button"
                            className="btn btn-success fw-bold d-flex align-items-center gap-2 shadow-sm"
                            onClick={() => setIsCheckinModalOpen(true)}
                        >
                            <i className="bi bi-qr-code-scan fs-6"></i>
                            <span>Escanear Pases / Check-in</span>
                            <span className="badge bg-white text-success fw-bold ms-1">
                                {metrics.totalRedeemed || 0} / 20
                            </span>
                        </button>

                        <button
                            type="button"
                            className="btn btn-outline-dark fw-bold d-flex align-items-center gap-2"
                            onClick={() => setIsConfigModalOpen(true)}
                        >
                            <i className="bi bi-sliders text-danger"></i>
                            <span>Configuración</span>
                        </button>

                        <button
                            type="button"
                            className="btn btn-outline-success fw-bold d-flex align-items-center gap-2"
                            onClick={handleExportExcel}
                            disabled={isExportingExcel}
                        >
                            <i className="bi bi-file-earmark-excel-fill text-success"></i>
                            <span>{isExportingExcel ? 'Exportando...' : 'Descargar Excel'}</span>
                        </button>

                        <button
                            type="button"
                            className="btn btn-outline-secondary d-flex align-items-center gap-1"
                            onClick={refreshAllData}
                            disabled={isLoading}
                            title="Sincronizar datos del servidor"
                        >
                            <i className={`bi bi-arrow-clockwise ${isLoading ? 'spin-animation' : ''}`}></i>
                            <span>Actualizar</span>
                        </button>
                    </div>
                </div>

                {/* 2. TARJETA DESTACADA DE MONITOREO DE BOLETOS GANADORES (SPLIT CARD) */}
                <div className="card p-3 border-0 rounded-3 shadow-sm bg-white text-center mb-4">
                    <div className="row g-3 align-items-center">
                        <div className="col-12 col-md-6 border-end-md">
                            <div className="d-flex align-items-center justify-content-center gap-2">
                                <i className="bi bi-award-fill text-warning fs-3"></i>
                                <div className="text-start">
                                    <div className="text-muted small fw-semibold">Boletos Ganadores Asignados</div>
                                    <div className="fs-4 fw-bold text-dark">
                                        {winnersCount} <span className="text-muted fs-6">/ 20 boletos (10 del 23 Oct + 10 del 31 Oct)</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-12 col-md-6">
                            <div className="d-flex align-items-center justify-content-center gap-2">
                                <i className="bi bi-check-circle-fill text-success fs-3"></i>
                                <div className="text-start">
                                    <div className="text-muted small fw-semibold">Boletos Canjeados / Ingresados</div>
                                    <div className="fs-4 fw-bold text-success">
                                        {metrics.totalRedeemed || 0} <span className="text-muted fs-6">/ {winnersCount > 0 ? winnersCount : 20}</span>
                                    </div>
                                </div>
                                {metrics.totalRedeemed > 0 && (
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-warning ms-3 px-2 py-1 fw-semibold d-inline-flex align-items-center gap-1 shadow-sm"
                                        onClick={() => setIsResetRedeemedModalOpen(true)}
                                        title="Reiniciar todos los canjes de prueba (revertir a pendiente con contraseña)"
                                    >
                                        <i className="bi bi-arrow-counterclockwise"></i>
                                        <span>Limpiar Pruebas</span>
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* 3. CARDS DE KPIS DEL FUNNEL (PISTA HORIZONTAL DESPLAZABLE) */}
                <div
                    className="d-flex flex-nowrap overflow-x-auto gap-3 pb-3 mb-4 pt-1"
                    style={{ scrollbarWidth: 'thin', WebkitOverflowScrolling: 'touch' }}
                >
                    {/* Escaneos QR */}
                    <div className="card border-0 shadow-sm rounded-3 p-3 bg-white flex-shrink-0" style={{ minWidth: '175px' }}>
                        <i className="bi bi-qr-code text-danger fs-3 mb-1"></i>
                        <div className="text-secondary small fw-semibold text-truncate">Escaneos QR</div>
                        <div className="fs-3 fw-bold text-dark mt-1">{metrics.totalQrScans ?? 0}</div>
                        <small className="text-muted text-nowrap">Accesos por QR</small>
                    </div>

                    {/* Ingresos Página */}
                    <div className="card border-0 shadow-sm rounded-3 p-3 bg-white flex-shrink-0" style={{ minWidth: '175px' }}>
                        <i className="bi bi-globe text-primary fs-3 mb-1"></i>
                        <div className="text-secondary small fw-semibold text-truncate">Ingresos Página</div>
                        <div className="fs-3 fw-bold text-dark mt-1">{metrics.totalPageViews ?? 0}</div>
                        <small className="text-muted text-nowrap">Visitas totales</small>
                    </div>

                    {/* Inicios Registro */}
                    <div className="card border-0 shadow-sm rounded-3 p-3 bg-white flex-shrink-0" style={{ minWidth: '175px' }}>
                        <i className="bi bi-pencil-square text-warning fs-3 mb-1"></i>
                        <div className="text-secondary small fw-semibold text-truncate">Inicios Registro</div>
                        <div className="fs-3 fw-bold text-dark mt-1">{metrics.totalFormStarts ?? 0}</div>
                        <small className="text-muted text-nowrap">Formularios iniciados</small>
                    </div>

                    {/* Registros Completos */}
                    <div className="card border-0 shadow-sm rounded-3 p-3 bg-white flex-shrink-0" style={{ minWidth: '175px' }}>
                        <i className="bi bi-check-circle-fill text-success fs-3 mb-1"></i>
                        <div className="text-secondary small fw-semibold text-truncate">Registros Completos</div>
                        <div className="fs-3 fw-bold text-dark mt-1">{metrics.totalRegistersCompleted || totalCount}</div>
                        <small className="text-muted text-nowrap">Pases emitidos</small>
                    </div>

                    {/* Pases Obtenidos (Ganadores) */}
                    <div className="card border-0 shadow-sm rounded-3 p-3 bg-white flex-shrink-0" style={{ minWidth: '175px' }}>
                        <i className="bi bi-award-fill text-warning fs-3 mb-1"></i>
                        <div className="text-secondary small fw-semibold text-truncate">Pases Ganadores</div>
                        <div className="fs-3 fw-bold text-dark mt-1">
                            {winnersCount} <span className="text-muted fs-6">/ {config?.maxWinners || 20}</span>
                        </div>
                        <small className="text-muted text-nowrap">Premios asignados</small>
                    </div>

                    {/* Pases Guardados */}
                    <div className="card border-0 shadow-sm rounded-3 p-3 bg-white flex-shrink-0" style={{ minWidth: '175px' }}>
                        <i className="bi bi-download text-success fs-3 mb-1"></i>
                        <div className="text-secondary small fw-semibold text-truncate">Pases Guardados</div>
                        <div className="fs-3 fw-bold text-dark mt-1">{metrics.totalPassesSaved ?? 0}</div>
                        <small className="text-muted text-nowrap">Descargas en móvil</small>
                    </div>

                    {/* Pases Recuperados */}
                    <div className="card border-0 shadow-sm rounded-3 p-3 bg-white flex-shrink-0" style={{ minWidth: '175px' }}>
                        <i className="bi bi-arrow-repeat text-info fs-3 mb-1"></i>
                        <div className="text-secondary small fw-semibold text-truncate">Pases Recuperados</div>
                        <div className="fs-3 fw-bold text-dark mt-1">{metrics.totalPassesRecovered ?? 0}</div>
                        <small className="text-muted text-nowrap">Consultas exitosas</small>
                    </div>
                </div>

                {/* 4. TABLA DE PARTICIPANTES REGISTRADOS */}
                <div className="card border-0 shadow-sm rounded-4 overflow-hidden bg-white mb-4">
                    {/* Header y Filtros de la Tabla */}
                    <div className="card-header bg-white py-3 px-4 border-bottom">
                        <div className="row g-3 align-items-center justify-content-between">
                            <div className="col-12 col-md-5">
                                <form onSubmit={handleSearchSubmit} className="input-group">
                                    <span className="input-group-text bg-light border-end-0">
                                        <i className="bi bi-search text-secondary"></i>
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control bg-light border-start-0 border-end-0"
                                        placeholder="Buscar por aficionado, ticket, correo o folio..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                    {searchTerm && (
                                        <button
                                            type="button"
                                            className="btn btn-light border-top border-bottom border-start-0"
                                            onClick={handleClearSearch}
                                            title="Limpiar búsqueda"
                                        >
                                            <i className="bi bi-x-lg text-muted"></i>
                                        </button>
                                    )}
                                    <button type="submit" className="btn btn-outline-danger fw-semibold">
                                        Buscar
                                    </button>
                                </form>
                            </div>

                            <div className="col-12 col-md-7 d-flex align-items-center justify-content-md-end gap-2 flex-wrap">
                                <label htmlFor="xtp-status-filter" className="small fw-semibold text-secondary text-nowrap mb-0 d-flex align-items-center gap-1">
                                    <i className="bi bi-funnel-fill text-danger"></i>
                                    <span>Filtrar por:</span>
                                </label>
                                <select
                                    id="xtp-status-filter"
                                    className="form-select form-select-sm border-secondary-subtle fw-semibold text-dark shadow-sm"
                                    style={{ minWidth: '220px', maxWidth: '320px' }}
                                    value={statusFilter}
                                    onChange={(e) => handleStatusFilterChange(e.target.value)}
                                >
                                    <option value="ALL">Todos los registros</option>
                                    <option value="WINNERS">Ganadores de Boleto</option>
                                    <option value="NON_WINNERS">Tickets Generales (No Ganadores)</option>
                                    <option value="REDEEMED">Canjeados en Tienda/Estadio</option>
                                    <option value="DISQUALIFIED">Descalificados</option>
                                </select>
                            </div>
                        </div>

                        {/* Leyenda sutil de zona horaria */}
                        <div className="d-flex align-items-center justify-content-between mt-2 pt-2 border-top border-light-subtle">
                            <div className="text-muted small d-flex align-items-center gap-1" style={{ fontSize: '0.8rem' }}>
                                <i className="bi bi-clock-history text-danger"></i>
                                <span>* Fechas y horarios mostrados en la zona horaria local de Tijuana (PST/PDT). Haz clic en las columnas para ordenar la tabla.</span>
                            </div>
                        </div>
                    </div>

                    {/* Tabla de Resultados */}
                    <div className="table-responsive">
                        <table className="table table-hover align-middle mb-0">
                            <thead className="table-light small text-uppercase fw-bold text-secondary">
                                <tr>
                                    <th className="px-3 py-3">#</th>
                                    <th
                                        className="text-nowrap"
                                        style={{ cursor: 'pointer', userSelect: 'none' }}
                                        onClick={() => handleSort('createdAtUtc')}
                                        title="Ordenar por fecha de participación"
                                    >
                                        <span className="me-1">Fecha de participación</span>
                                        <i className={`bi ${sortBy === 'createdAtUtc' ? (sortOrder === 'asc' ? 'bi-sort-up text-danger fw-bold' : 'bi-sort-down text-danger fw-bold') : 'bi-arrow-down-up text-muted opacity-50'}`}></i>
                                    </th>
                                    <th
                                        className="text-nowrap"
                                        style={{ cursor: 'pointer', userSelect: 'none' }}
                                        onClick={() => handleSort('name')}
                                        title="Ordenar alfabéticamente por aficionado"
                                    >
                                        <span className="me-1">Nombre</span>
                                        <i className={`bi ${sortBy === 'name' ? (sortOrder === 'asc' ? 'bi-sort-alpha-down text-danger fw-bold' : 'bi-sort-alpha-down-alt text-danger fw-bold') : 'bi-arrow-down-up text-muted opacity-50'}`}></i>
                                    </th>
                                    <th>Contacto</th>
                                    <th
                                        className="text-nowrap"
                                        style={{ cursor: 'pointer', userSelect: 'none' }}
                                        onClick={() => handleSort('ticketNumber')}
                                        title="Ordenar por número de ticket"
                                    >
                                        <span className="me-1">Ticket</span>
                                        <i className={`bi ${sortBy === 'ticketNumber' ? (sortOrder === 'asc' ? 'bi-sort-numeric-down text-danger fw-bold' : 'bi-sort-numeric-down-alt text-danger fw-bold') : 'bi-arrow-down-up text-muted opacity-50'}`}></i>
                                    </th>
                                    <th
                                        className="text-nowrap"
                                        style={{ cursor: 'pointer', userSelect: 'none' }}
                                        onClick={() => handleSort('passCode')}
                                        title="Ordenar por folio de pase"
                                    >
                                        <span className="me-1">Folio Pase</span>
                                        <i className={`bi ${sortBy === 'passCode' ? (sortOrder === 'asc' ? 'bi-sort-down text-danger fw-bold' : 'bi-sort-up text-danger fw-bold') : 'bi-arrow-down-up text-muted opacity-50'}`}></i>
                                    </th>
                                    <th
                                        className="text-nowrap"
                                        style={{ cursor: 'pointer', userSelect: 'none' }}
                                        onClick={() => handleSort('isWinner')}
                                        title="Ordenar por beneficio / ganador"
                                    >
                                        <span className="me-1">Beneficio / Premio</span>
                                        <i className={`bi ${sortBy === 'isWinner' ? (sortOrder === 'asc' ? 'bi-sort-down text-danger fw-bold' : 'bi-sort-up text-danger fw-bold') : 'bi-arrow-down-up text-muted opacity-50'}`}></i>
                                    </th>
                                    <th
                                        className="text-nowrap"
                                        style={{ cursor: 'pointer', userSelect: 'none' }}
                                        onClick={() => handleSort('hasRedeemed')}
                                        title="Ordenar por estado de canje"
                                    >
                                        <span className="me-1">Estado de Canje</span>
                                        <i className={`bi ${sortBy === 'hasRedeemed' ? (sortOrder === 'asc' ? 'bi-sort-down text-danger fw-bold' : 'bi-sort-up text-danger fw-bold') : 'bi-arrow-down-up text-muted opacity-50'}`}></i>
                                    </th>
                                    <th>Captura</th>
                                    <th className="text-center px-3">Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading ? (
                                    <tr>
                                        <td colSpan="10" className="text-center py-5">
                                            <div className="spinner-border text-danger" role="status"></div>
                                            <div className="text-muted small mt-2">Consultando participantes...</div>
                                        </td>
                                    </tr>
                                ) : participants.length === 0 ? (
                                    <tr>
                                        <td colSpan="10" className="text-center py-5 text-muted">
                                            <i className="bi bi-inbox fs-1 d-block mb-2 text-secondary"></i>
                                            No se encontraron participantes registrados con los filtros seleccionados.
                                        </td>
                                    </tr>
                                ) : (
                                    participants.map((p, idx) => (
                                        <tr key={p.id} className={p.isDisqualified ? 'table-danger' : ''}>
                                            <td className="px-3 py-3 fw-bold text-secondary">
                                                {(page - 1) * pageSize + idx + 1}
                                            </td>
                                            <td className="small text-nowrap">
                                                {FormatTijuanaDateTime(p.createdAtUtc)}
                                            </td>
                                            <td>
                                                <div className="fw-bold text-dark">{p.name}</div>
                                            </td>
                                            <td className="small">
                                                <div className="text-dark">{p.email}</div>
                                                <div className="text-secondary">{p.phone}</div>
                                            </td>
                                            <td>
                                                <span className="badge bg-light text-dark border font-monospace px-2 py-1">
                                                    {p.ticketNumber}
                                                </span>
                                            </td>
                                            <td>
                                                <span className="badge bg-warning text-dark fw-bold font-monospace shadow-sm px-2 py-1">
                                                    {p.passCode}
                                                </span>
                                            </td>
                                            <td>
                                                {p.isWinner ? (
                                                    <span className="badge bg-success-subtle text-success border border-success fw-bold px-2 py-1 shadow-sm" title={p.prizeName || 'Boleto Ganador'}>
                                                        <i className="bi bi-trophy-fill me-1"></i>
                                                        {p.prizeName ? p.prizeName : 'Boleto Ganador'}
                                                    </span>
                                                ) : (
                                                    <span className="badge bg-light text-secondary border px-2 py-1">
                                                        🎟️ Ticket Registrado
                                                    </span>
                                                )}
                                            </td>
                                            <td>
                                                {p.isDisqualified ? (
                                                    <span className="badge bg-danger text-white px-2 py-1" title={p.disqualifiedReason || ''}>
                                                        <i className="bi bi-slash-circle-fill me-1"></i> Descalificado
                                                    </span>
                                                ) : p.hasRedeemed ? (
                                                    <span className="badge bg-success text-white px-2 py-1 shadow-sm">
                                                        <i className="bi bi-check2-all me-1"></i> Canjeado
                                                        {p.redeemedAtUtc && (
                                                            <div className="text-white-50 fw-normal" style={{ fontSize: '0.65rem' }}>
                                                                {FormatTijuanaDateTime(p.redeemedAtUtc)}
                                                            </div>
                                                        )}
                                                    </span>
                                                ) : (
                                                    <span className="badge bg-secondary-subtle text-secondary border px-2 py-1">
                                                        Pendiente
                                                    </span>
                                                )}
                                            </td>
                                            <td>
                                                {p.hasSavedScreenshot ? (
                                                    <span className="badge bg-info-subtle text-info-emphasis border border-info-subtle" title={`${p.screenshotCount} capturas`}>
                                                        <i className="bi bi-camera me-1"></i> Guardada ({p.screenshotCount})
                                                    </span>
                                                ) : (
                                                    <span className="text-muted small">No</span>
                                                )}
                                            </td>
                                            <td className="text-center px-3 text-nowrap">
                                                {p.isDisqualified ? (
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-success fw-semibold"
                                                        onClick={() => handleUndisqualify(p)}
                                                        title="Restituir participante y reactivar su pase"
                                                    >
                                                        <i className="bi bi-arrow-counterclockwise me-1"></i> Restituir
                                                    </button>
                                                ) : (
                                                    <div className="d-flex align-items-center justify-content-center gap-1">
                                                        {!p.hasRedeemed && (
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-outline-success fw-semibold"
                                                                onClick={async () => {
                                                                    if (window.confirm(`¿Confirmar entrega y canje físico del beneficio para ${p.name} (Folio: ${p.passCode})?`)) {
                                                                        try {
                                                                            const res = await RedeemParticipant({
                                                                                passCode: p.passCode,
                                                                                operatorName: 'Staff Operativo (Tablero)'
                                                                            });
                                                                            if (res && res.success) {
                                                                                showMessage({
                                                                                    message: `¡Beneficio entregado a ${p.name}!`,
                                                                                    type: MESSAGE_TYPE.SUCCESS
                                                                                });
                                                                                PlayXtpAudio('success');
                                                                                loadParticipants(page);
                                                                                loadMetrics();
                                                                            } else {
                                                                                showMessage({
                                                                                    message: res?.message || 'Error al canjear.',
                                                                                    type: MESSAGE_TYPE.ERROR
                                                                                });
                                                                                PlayXtpAudio('error');
                                                                            }
                                                                        } catch (err) {
                                                                            showMessage({
                                                                                message: 'Error de red al procesar canje.',
                                                                                type: MESSAGE_TYPE.ERROR
                                                                            });
                                                                        }
                                                                    }
                                                                }}
                                                                title="Validar y marcar boleto como canjeado"
                                                            >
                                                                <i className="bi bi-check2-circle me-1"></i> Canjear
                                                            </button>
                                                        )}
                                                        {p.hasRedeemed && (
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-outline-warning fw-semibold d-inline-flex align-items-center gap-1 shadow-sm"
                                                                onClick={() => handleUnredeem(p)}
                                                                title="Desmarcar canje (revertir a pendiente con confirmación de seguridad)"
                                                            >
                                                                <i className="bi bi-arrow-counterclockwise"></i>
                                                                <span>Desmarcar</span>
                                                            </button>
                                                        )}
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-primary fw-semibold d-inline-flex align-items-center gap-1 shadow-sm"
                                                            onClick={() => {
                                                                setPassModalParticipant(p);
                                                                setPassModalIndex(idx);
                                                            }}
                                                            title="Abrir vista previa del pase oficial y opciones para guardar en galería"
                                                        >
                                                            <i className="bi bi-card-image"></i>
                                                            <span>Generar pase</span>
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-danger fw-semibold d-inline-flex align-items-center gap-1 shadow-sm"
                                                            onClick={() => setDisqualifyTarget(p)}
                                                            title="Descalificar participante"
                                                        >
                                                            <i className="bi bi-slash-circle"></i>
                                                            <span>Descalificar</span>
                                                        </button>
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Paginación */}
                    {totalPages > 1 && (
                        <div className="card-footer bg-white px-4 py-3 d-flex align-items-center justify-content-between border-top">
                            <span className="small text-secondary">
                                Mostrando {(page - 1) * pageSize + 1} a {Math.min(page * pageSize, totalCount)} de {totalCount} registros
                            </span>
                            <div className="d-flex align-items-center gap-2">
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary"
                                    disabled={page <= 1 || isLoading}
                                    onClick={() => loadParticipants(page - 1)}
                                >
                                    <i className="bi bi-chevron-left"></i> Anterior
                                </button>
                                <span className="small fw-bold px-2 text-dark">
                                    {page} / {totalPages}
                                </span>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary"
                                    disabled={page >= totalPages || isLoading}
                                    onClick={() => loadParticipants(page + 1)}
                                >
                                    Siguiente <i className="bi bi-chevron-right"></i>
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* 5. SECCIÓN PLEGABLE 1: CONTROL DE REGISTROS (HABILITAR / INHABILITAR) */}
                <div className="card border-0 rounded-3 shadow-sm bg-white mt-4">
                    <div
                        className="card-header bg-light border-0 py-3 d-flex justify-content-between align-items-center"
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => setIsRegistrationControlOpen(!isRegistrationControlOpen)}
                    >
                        <h3 className="h6 fw-bold mb-0 text-dark d-flex align-items-center gap-2">
                            <i className="bi bi-toggle2-on text-primary fs-5"></i>
                            Control de Registros para Aficionados (Abrir / Pausar Dinámica)
                        </h3>
                        <i className={`bi ${isRegistrationControlOpen ? 'bi-chevron-up' : 'bi-chevron-down'} fs-6 text-secondary`}></i>
                    </div>
                    {isRegistrationControlOpen && (
                        <div className="card-body p-4 border-top">
                            <p className="text-secondary small mb-3">
                                Desde este control puedes habilitar o suspender temporalmente el formulario de registro de tickets en vivo.
                                Cuando está inactivo, los aficionados verán un aviso amigable de que los registros están en pausa.
                            </p>
                            <button
                                type="button"
                                onClick={() => triggerToggleCampaignActive(!config.isCampaignActive)}
                                className={`btn w-100 py-3 fw-bold d-flex align-items-center justify-content-center gap-2 transition-all ${
                                    config.isCampaignActive
                                        ? 'btn-outline-secondary'
                                        : 'btn-outline-danger'
                                }`}
                            >
                                <i className={`bi ${config.isCampaignActive ? 'bi-check-circle-fill text-success' : 'bi-slash-circle-fill text-danger'}`}></i>
                                <span>
                                    {config.isCampaignActive
                                        ? 'Registros Activos (La afición puede registrar tickets en /tpx2026) — Clic para Pausar'
                                        : 'Registros Pausados (Formulario cerrado para aficionados) — Clic para Abrir'}
                                </span>
                            </button>
                        </div>
                    )}
                </div>

                {/* 6. SECCIÓN PLEGABLE 2: ZONA DE RIESGO (BORRADO DE REGISTROS CON CONTRASEÑA) */}
                <div className="card border-0 rounded-3 shadow-sm bg-white mt-4">
                    <div
                        className="card-header bg-light border-0 py-3 d-flex justify-content-between align-items-center"
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => setIsDangerZoneOpen(!isDangerZoneOpen)}
                    >
                        <h3 className="h6 fw-bold mb-0 text-danger d-flex align-items-center gap-2">
                            <i className="bi bi-shield-slash-fill text-danger fs-5"></i>
                            Zona Peligrosa: Reinicio Maestro de Dinámica (Protegido con Contraseña)
                        </h3>
                        <i className={`bi ${isDangerZoneOpen ? 'bi-chevron-up' : 'bi-chevron-down'} fs-6 text-secondary`}></i>
                    </div>
                    {isDangerZoneOpen && (
                        <div className="card-body p-4 border-top">
                            <div className="bg-danger-subtle p-3 rounded-3 border border-danger border-opacity-25 mb-3">
                                <h4 className="fs-6 fw-bold text-danger mb-1 d-flex align-items-center gap-2">
                                    <i className="bi bi-exclamation-triangle-fill"></i>
                                    ¡Atención Administrador!
                                </h4>
                                <p className="small text-danger-emphasis mb-0">
                                    Esta operación purgará permanentemente la base de datos de todos los aficionados registrados,
                                    sus pases emitidos y la telemetría, restableciendo a cero el padrón oficial de la dinámica.
                                    Para evitar clics accidentales, se requerirá la contraseña maestra de seguridad autorizada.
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setIsResetModalOpen(true)}
                                className="btn btn-danger w-100 py-2.5 fw-bold d-flex align-items-center justify-content-center gap-2 shadow-sm"
                            >
                                <i className="bi bi-trash-fill"></i>
                                <span>Reiniciar Dinámica y Borrar Todos los Registros</span>
                            </button>

                            <hr className="my-4" />

                            <div className="bg-warning-subtle p-3 rounded-3 border border-warning border-opacity-25 mb-3">
                                <h4 className="fs-6 fw-bold text-dark mb-1 d-flex align-items-center gap-2">
                                    <i className="bi bi-shield-lock-fill text-warning fs-5"></i>
                                    Limpieza de Canjes de Prueba (Pre-Evento)
                                </h4>
                                <p className="small text-secondary mb-0">
                                    Si realizaste pruebas de escaneo con lectores o códigos QR, puedes restablecer todos los boletos a estado <strong>"Pendiente"</strong> antes del evento real sin borrar ningún participante ni afectar las asignaciones de boletos ganadores. Requiere contraseña maestra.
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setIsResetRedeemedModalOpen(true)}
                                className="btn btn-outline-warning text-dark fw-bold w-100 py-2.5 d-flex align-items-center justify-content-center gap-2 shadow-sm"
                            >
                                <i className="bi bi-arrow-counterclockwise"></i>
                                <span>Reiniciar Todos los Canjes de Prueba a Pendiente (Requiere Contraseña)</span>
                            </button>
                        </div>
                    )}
                </div>

                {/* 7. SECCIÓN PLEGABLE 3: ASISTENCIA Y CIBERSEGURIDAD EN TIENDA */}
                <div className="card border-0 rounded-3 shadow-sm bg-white mt-4 mb-4">
                    <div
                        className="card-header bg-light border-0 py-3 d-flex justify-content-between align-items-center"
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => setIsToolsOpen(!isToolsOpen)}
                    >
                        <h3 className="h6 fw-bold mb-0 text-dark d-flex align-items-center gap-2">
                            <i className="bi bi-shield-check text-dark fs-5"></i>
                            Herramientas de Asistencia (Desbloqueo de Ciberseguridad por Error Humano)
                        </h3>
                        <i className={`bi ${isToolsOpen ? 'bi-chevron-up' : 'bi-chevron-down'} fs-6 text-secondary`}></i>
                    </div>
                    {isToolsOpen && (
                        <div className="card-body p-4 border-top">
                            <p className="text-secondary small mb-3">
                                Si un aficionado se equivoca en la tienda y bloquea temporalmente su ticket o sesión por exceso de intentos fallidos, puedes liberarlo al instante aquí.
                            </p>
                            <div className="row g-3">
                                <div className="col-12 col-md-6">
                                    <form onSubmit={handleQuickUnblockTicket} className="p-3 bg-light rounded-3 border">
                                        <label className="form-label fw-bold small text-dark">Desbloquear Ticket</label>
                                        <div className="input-group">
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Ingresa # Ticket (ej. TICK-00123)"
                                                value={unblockTicketQuery}
                                                onChange={(e) => setUnblockTicketQuery(e.target.value)}
                                                disabled={isUnblocking}
                                            />
                                            <button type="submit" className="btn btn-danger fw-semibold px-3" disabled={isUnblocking || !unblockTicketQuery.trim()}>
                                                Desbloquear Ticket
                                            </button>
                                        </div>
                                    </form>
                                </div>

                                <div className="col-12 col-md-6">
                                    <form onSubmit={handleQuickUnblockSession} className="p-3 bg-light rounded-3 border">
                                        <label className="form-label fw-bold small text-dark">Desbloquear Sesión / Dispositivo</label>
                                        <div className="input-group">
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Ingresa UUID de Sesión"
                                                value={unblockSessionQuery}
                                                onChange={(e) => setUnblockSessionQuery(e.target.value)}
                                                disabled={isUnblocking}
                                            />
                                            <button type="submit" className="btn btn-secondary fw-semibold px-3" disabled={isUnblocking || !unblockSessionQuery.trim()}>
                                                Desbloquear Sesión
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* 8. SECCIÓN PLEGABLE 4: MÉTRICAS DEL PORTAL DE CONSULTA Y DESCARGA DE PASES (/passtpx) */}
                <div className="card border-0 rounded-3 shadow-sm bg-white mt-4 mb-4">
                    <div
                        className="card-header bg-light border-0 py-3 d-flex justify-content-between align-items-center"
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => setIsPassTpxMetricsOpen(!isPassTpxMetricsOpen)}
                    >
                        <div className="d-flex align-items-center gap-2">
                            <i className="bi bi-search text-danger fs-5"></i>
                            <div>
                                <h3 className="h6 fw-bold mb-0 text-dark">
                                    Métricas del Portal de Consulta y Descarga de Pases (/passtpx)
                                </h3>
                                <small className="text-secondary">
                                    Estadísticas independientes de aficionados que buscan o descargan su pase digital
                                </small>
                            </div>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-danger d-inline-flex align-items-center gap-1 shadow-sm px-2 py-1"
                                onClick={handleRefreshPassFanMetrics}
                                disabled={isRefreshingPassFanMetrics}
                                title="Actualizar estadísticas de /passtpx en tiempo real"
                            >
                                {isRefreshingPassFanMetrics ? (
                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ width: '0.85rem', height: '0.85rem' }}></span>
                                ) : (
                                    <i className="bi bi-arrow-clockwise"></i>
                                )}
                                <span className="small fw-semibold">{isRefreshingPassFanMetrics ? 'Actualizando...' : 'Actualizar'}</span>
                            </button>
                            <span className="badge bg-danger-subtle text-danger border border-danger-subtle fw-semibold">
                                Portal Fan
                            </span>
                            <i className={`bi ${isPassTpxMetricsOpen ? 'bi-chevron-up' : 'bi-chevron-down'} fs-6 text-secondary`}></i>
                        </div>
                    </div>
                    {isPassTpxMetricsOpen && (
                        <div className="card-body p-3 p-md-4 border-top">
                            <div
                                className="d-flex flex-nowrap overflow-x-auto gap-3 pb-2 pt-1"
                                style={{ scrollbarWidth: 'thin', WebkitOverflowScrolling: 'touch' }}
                            >
                                {/* Escaneos QR */}
                                <div className="card border-0 shadow-sm rounded-3 p-3 bg-light flex-shrink-0" style={{ minWidth: '175px' }}>
                                    <i className="bi bi-qr-code-scan text-danger fs-3 mb-1"></i>
                                    <div className="text-secondary small fw-semibold text-truncate">Escaneos QR</div>
                                    <div className="fs-3 fw-bold text-dark mt-1">{passFan?.totalQrScans ?? 0}</div>
                                    <small className="text-muted text-nowrap">Vía enlace ?s=qr</small>
                                </div>

                                {/* Ingresos al Portal */}
                                <div className="card border-0 shadow-sm rounded-3 p-3 bg-light flex-shrink-0" style={{ minWidth: '175px' }}>
                                    <i className="bi bi-globe text-primary fs-3 mb-1"></i>
                                    <div className="text-secondary small fw-semibold text-truncate">Ingresos Portal</div>
                                    <div className="fs-3 fw-bold text-dark mt-1">{passFan?.totalPageViews ?? 0}</div>
                                    <small className="text-muted text-nowrap">Visitas a /passtpx</small>
                                </div>

                                {/* Inicios de Búsqueda */}
                                <div className="card border-0 shadow-sm rounded-3 p-3 bg-light flex-shrink-0" style={{ minWidth: '175px' }}>
                                    <i className="bi bi-cursor-fill text-warning fs-3 mb-1"></i>
                                    <div className="text-secondary small fw-semibold text-truncate">Inicios Búsqueda</div>
                                    <div className="fs-3 fw-bold text-dark mt-1">{passFan?.totalSearchStarts ?? 0}</div>
                                    <small className="text-muted text-nowrap">Clics en buscador</small>
                                </div>

                                {/* Consultas Realizadas */}
                                <div className="card border-0 shadow-sm rounded-3 p-3 bg-light flex-shrink-0" style={{ minWidth: '175px' }}>
                                    <i className="bi bi-search text-info fs-3 mb-1"></i>
                                    <div className="text-secondary small fw-semibold text-truncate">Consultas Realizadas</div>
                                    <div className="fs-3 fw-bold text-dark mt-1">{passFan?.totalSearchQueries ?? 0}</div>
                                    <small className="text-muted text-nowrap">Búsquedas enviadas</small>
                                </div>

                                {/* Pases Obtenidos */}
                                <div className="card border-0 shadow-sm rounded-3 p-3 bg-light flex-shrink-0" style={{ minWidth: '175px' }}>
                                    <i className="bi bi-award-fill text-success fs-3 mb-1"></i>
                                    <div className="text-secondary small fw-semibold text-truncate">Pases Obtenidos</div>
                                    <div className="fs-3 fw-bold text-success mt-1">{passFan?.totalPassesFound ?? 0}</div>
                                    <small className="text-muted text-nowrap">Localizados con éxito</small>
                                </div>

                                {/* No Encontrados */}
                                <div className="card border-0 shadow-sm rounded-3 p-3 bg-light flex-shrink-0" style={{ minWidth: '175px' }}>
                                    <i className="bi bi-question-circle text-secondary fs-3 mb-1"></i>
                                    <div className="text-secondary small fw-semibold text-truncate">No Encontrados</div>
                                    <div className="fs-3 fw-bold text-secondary mt-1">{passFan?.totalNotFound ?? 0}</div>
                                    <small className="text-muted text-nowrap">Sin coincidencia</small>
                                </div>

                                {/* Pases Guardados en Galería */}
                                <div className="card border-0 shadow-sm rounded-3 p-3 bg-light flex-shrink-0" style={{ minWidth: '175px' }}>
                                    <i className="bi bi-download text-dark fs-3 mb-1"></i>
                                    <div className="text-secondary small fw-semibold text-truncate">Pases Guardados</div>
                                    <div className="fs-3 fw-bold text-dark mt-1">{passFan?.totalPassesSaved ?? 0}</div>
                                    <small className="text-muted text-nowrap">Descargas en galería</small>
                                </div>

                                {/* Efectividad de Búsqueda */}
                                <div className="card border-0 shadow-sm rounded-3 p-3 bg-light flex-shrink-0" style={{ minWidth: '175px' }}>
                                    <i className="bi bi-pie-chart-fill text-danger fs-3 mb-1"></i>
                                    <div className="text-secondary small fw-semibold text-truncate">Efectividad Búsqueda</div>
                                    <div className="fs-3 fw-bold text-danger mt-1">{passFan?.searchSuccessRatePercent ?? 0}%</div>
                                    <small className="text-muted text-nowrap">Éxito en consultas</small>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* 9. SECCIÓN PLEGABLE 5: CONTROL DE DISPONIBILIDAD DEL PORTAL (/passtpx) */}
                <div className="card border-0 rounded-3 shadow-sm bg-white mt-4">
                    <div
                        className="card-header bg-light border-0 py-3 d-flex justify-content-between align-items-center"
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => setIsPassLookupControlOpen(!isPassLookupControlOpen)}
                    >
                        <h3 className="h6 fw-bold mb-0 text-dark d-flex align-items-center gap-2">
                            <i className="bi bi-toggles2 text-primary fs-5"></i>
                            Control de Disponibilidad: Portal de Consultas (/passtpx)
                        </h3>
                        <div className="d-flex align-items-center gap-2">
                            <span className={`badge ${config.isPassLookupActive ? 'bg-success' : 'bg-danger'} text-white fw-semibold`}>
                                {config.isPassLookupActive ? '🟢 Consultas Habilitadas' : '🔴 Consultas Pausadas'}
                            </span>
                            <i className={`bi ${isPassLookupControlOpen ? 'bi-chevron-up' : 'bi-chevron-down'} fs-6 text-secondary`}></i>
                        </div>
                    </div>
                    {isPassLookupControlOpen && (
                        <div className="card-body p-4 border-top">
                            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
                                <div>
                                    <h4 className="fs-6 fw-bold text-dark mb-1">
                                        Estado de la Búsqueda y Descarga en /passtpx
                                    </h4>
                                    <p className="text-secondary small mb-0">
                                        {config.isPassLookupActive
                                            ? 'El portal de aficionados está abierto para consultar folios y descargar pases oficiales.'
                                            : 'El portal de aficionados se encuentra pausado. Los aficionados verán un aviso de mantenimiento y el buscador estará deshabilitado.'}
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => triggerTogglePassLookupActive(!config.isPassLookupActive)}
                                    className={`btn ${config.isPassLookupActive ? 'btn-outline-danger' : 'btn-success'} fw-bold px-3 py-2 d-flex align-items-center gap-2 text-nowrap shadow-sm`}
                                >
                                    <i className={`bi ${config.isPassLookupActive ? 'bi-pause-circle-fill' : 'bi-play-circle-fill'}`}></i>
                                    <span>{config.isPassLookupActive ? 'Pausar Consultas en /passtpx' : 'Habilitar Consultas en /passtpx'}</span>
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* 10. SECCIÓN PLEGABLE 6: ZONA DE RIESGO (REINICIO DE ESTADÍSTICAS DE /passtpx CON CONTRASEÑA) */}
                <div className="card border-0 rounded-3 shadow-sm bg-white mt-4 mb-4">
                    <div
                        className="card-header bg-light border-0 py-3 d-flex justify-content-between align-items-center"
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => setIsPassTpxDangerZoneOpen(!isPassTpxDangerZoneOpen)}
                    >
                        <h3 className="h6 fw-bold mb-0 text-danger d-flex align-items-center gap-2">
                            <i className="bi bi-shield-slash-fill text-danger fs-5"></i>
                            Zona Peligrosa: Reiniciar Estadísticas de /passtpx (Protegido con Contraseña)
                        </h3>
                        <i className={`bi ${isPassTpxDangerZoneOpen ? 'bi-chevron-up' : 'bi-chevron-down'} fs-6 text-secondary`}></i>
                    </div>
                    {isPassTpxDangerZoneOpen && (
                        <div className="card-body p-4 border-top">
                            <div className="bg-danger-subtle p-3 rounded-3 border border-danger border-opacity-25 mb-3">
                                <h4 className="fs-6 fw-bold text-danger mb-1 d-flex align-items-center gap-2">
                                    <i className="bi bi-exclamation-triangle-fill"></i>
                                    ¡Atención Administrador!
                                </h4>
                                <p className="small text-danger-emphasis mb-0">
                                    Esta operación restablecerá a cero exclusivamente las estadísticas y registros de búsqueda de <strong>/passtpx</strong>.
                                    Para tu seguridad, <strong>los aficionados registrados y los boletos ganadores NO serán eliminados ni modificados</strong>.
                                    Requiere la contraseña maestra de seguridad autorizada.
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setIsResetPassTpxModalOpen(true)}
                                className="btn btn-outline-danger w-100 py-2.5 fw-bold d-flex align-items-center justify-content-center gap-2 shadow-sm"
                            >
                                <i className="bi bi-arrow-counterclockwise"></i>
                                <span>Reiniciar Estadísticas de /passtpx (Requiere Contraseña)</span>
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Modal de Confirmación Genérico */}
            {showConfirm && (
                <Confirm
                    message={confirmConfig.message}
                    actionToYesButton={confirmConfig.action}
                    textYesButton={confirmConfig.textYes}
                    textNoButton={confirmConfig.textNo}
                    setShowModal={setShowConfirm}
                    setConfirmData={() => {}}
                />
            )}

            {/* Modales Auxiliares */}
            <XtpConfigModal
                isOpen={isConfigModalOpen}
                onClose={() => setIsConfigModalOpen(false)}
                onConfigSaved={() => { refreshAllData(); }}
            />

            <XtpDisqualifyModal
                isOpen={Boolean(disqualifyTarget)}
                participant={disqualifyTarget}
                onClose={() => setDisqualifyTarget(null)}
                onDisqualified={() => { loadParticipants(page); loadMetrics(); }}
            />

            <XtpCheckinModal
                isOpen={isCheckinModalOpen}
                onClose={() => setIsCheckinModalOpen(false)}
                onRedeemSuccess={() => { loadParticipants(page); loadMetrics(); }}
            />

            <XtpResetModal
                isOpen={isResetModalOpen}
                onClose={() => setIsResetModalOpen(false)}
                onResetSuccess={() => { refreshAllData(); }}
                showMessage={showMessage}
            />

            <XtpResetRedeemedModal
                isOpen={isResetRedeemedModalOpen}
                onClose={() => setIsResetRedeemedModalOpen(false)}
                onResetSuccess={() => { refreshAllData(); }}
                showMessage={showMessage}
            />

            <XtpResetPassTpxModal
                isOpen={isResetPassTpxModalOpen}
                onClose={() => setIsResetPassTpxModalOpen(false)}
                onResetSuccess={() => { loadMetrics(); }}
                showMessage={showMessage}
            />

            {/* Modal para Generar Pase Oficial e interactuar como en /passtpx con soporte multipase */}
            <XtpGeneratePassModal
                isOpen={Boolean(passModalParticipant)}
                participant={passModalParticipant}
                participants={participants}
                currentIndex={passModalIndex}
                onNavigate={(newIdx) => {
                    if (newIdx >= 0 && newIdx < participants.length) {
                        setPassModalIndex(newIdx);
                        setPassModalParticipant(participants[newIdx]);
                    }
                }}
                onClose={() => {
                    setPassModalParticipant(null);
                    setPassModalIndex(-1);
                }}
            />
        </SecondaryBase>
    );
};

export default XtpBoard;
